import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

interface EmailSecurityAlert {
  id: string;
  domain: string;
  severity: string;
  alert_type: string;
  title: string;
  description: string;
  status: string;
  created_at: string;
}

interface AlertsResponse {
  data: EmailSecurityAlert[];
  hasMore?: boolean;
}

const ALERT_TYPE_LABELS: Record<string, string> = {
  spf_missing: 'SPF Missing',
  dkim_missing: 'DKIM Missing',
  dmarc_missing: 'DMARC Missing',
  blacklist_detected: 'Blacklisted',
  spf_failure: 'SPF Failure',
  dkim_failure: 'DKIM Failure',
  dmarc_failure: 'DMARC Failure',
  new_sending_source: 'New Sender',
  policy_change: 'Policy Change',
};

export function EmailAlertsPage() {
  const { data: alertsData, loading, error, refresh } = useApi<AlertsResponse>('/email-security/domains?action=alerts');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const alerts = alertsData?.data ?? [];

  const openAlerts = alerts.filter(a => a.status === 'open').length;
  const acknowledgedAlerts = alerts.filter(a => a.status === 'acknowledged').length;
  const resolvedAlerts = alerts.filter(a => a.status === 'resolved').length;

  async function handleResolve(alertId: string) {
    setActionLoading(alertId);
    try {
      await post(`/email-security/domains/${alertId}?action=resolve_alert`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resolve alert');
    } finally {
      setActionLoading(null);
    }
  }

  function severityBadge(severity: string) {
    const colors =
      severity === 'critical' ? 'bg-red-100 text-red-700' :
      severity === 'high' ? 'bg-orange-100 text-orange-700' :
      severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
      severity === 'low' ? 'bg-blue-100 text-blue-700' :
      'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{severity}</span>;
  }

  function statusBadge(status: string) {
    const colors =
      status === 'open' ? 'bg-red-100 text-red-700' :
      status === 'acknowledged' ? 'bg-yellow-100 text-yellow-700' :
      'bg-emerald-100 text-emerald-700';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{status}</span>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading email alerts...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Security Alerts</h2>
      <p className="text-gray-600 mb-6">View and manage email security alerts across all monitored domains.</p>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Open Alerts</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{openAlerts}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Acknowledged</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">{acknowledgedAlerts}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Resolved</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{resolvedAlerts}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Alerts</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{alerts.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Domain</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Detail</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Severity</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Time</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {alerts.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No email security alerts. Your domains are looking good!</td></tr>
            ) : alerts.map((a) => (
              <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{ALERT_TYPE_LABELS[a.alert_type] ?? a.alert_type}</td>
                <td className="px-4 py-3 text-gray-600">{a.domain}</td>
                <td className="px-4 py-3 text-gray-600">{a.description || a.title}</td>
                <td className="px-4 py-3">{severityBadge(a.severity)}</td>
                <td className="px-4 py-3">{statusBadge(a.status)}</td>
                <td className="px-4 py-3 text-gray-500">{new Date(a.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  {a.status !== 'resolved' && (
                    <button
                      onClick={() => handleResolve(a.id)}
                      disabled={actionLoading === a.id}
                      className="text-xs text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                    >
                      {actionLoading === a.id ? 'Resolving...' : 'Resolve'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
